import { createRoot } from "react-dom/client";
import { AuthenticatedShopifyExtensionClientProvider } from "../client/authenticated-client-context";
import { RedoShopifyRpcClientContextProvider } from "../client/redo-shopify-rpc-client";
import STYLE_MANAGER from "../style-manager";
import { OrderTrackingLogin } from "./order-tracking-login";

void import("./styles.css");

const DIV_ID = "redo-order-tracking-login";

export function initialize() {
  if (document.body) {
    setup();
  } else {
    document.addEventListener("DOMContentLoaded", () => setup());
  }
}

function setup() {
  const scan = () =>
    findAndPopulateContainers({ parentElement: document.body });

  const observer = new MutationObserver((mutationList) => {
    for (const mutation of mutationList) {
      for (const addedNode of mutation.addedNodes) {
        if (addedNode instanceof HTMLElement) {
          findAndPopulateContainers({ parentElement: addedNode });
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  scan();

  // Re-scan once the DOM is fully parsed, in case the initial scan ran before
  // the div was added and the MutationObserver missed it during parsing.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scan);
  }
}

function findAndPopulateContainers({
  parentElement,
}: {
  parentElement: HTMLElement;
}) {
  const element = parentElement.querySelector<HTMLElement>(
    `#${DIV_ID}:not([data-initialized="true"])`,
  );

  if (!element) {
    if (parentElement.id === DIV_ID && !parentElement.dataset.initialized) {
      populateContainer({ container: parentElement });
    }
    return;
  }

  populateContainer({ container: element });
}

function populateContainer({ container }: { container: HTMLElement }) {
  container.style.display = "unset";
  container.dataset.initialized = "true";

  const shadowHost = document.createElement("div");
  shadowHost.style.display = "contents";
  container.append(shadowHost);

  const shadowRoot = shadowHost.attachShadow({ mode: "open" });
  STYLE_MANAGER.register(shadowRoot);

  const root = createRoot(shadowRoot);

  root.render(
    <AuthenticatedShopifyExtensionClientProvider
      baseUrl={
        globalThis.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
        process.env.REDO_SHOPIFY_SERVER_URL!
      }
      storeUrl={
        globalThis.redoStorefront?.storeUrl ??
        globalThis.Shopify?.shop ??
        globalThis.Shopify?.Checkout?.apiHost
      }
    >
      <RedoShopifyRpcClientContextProvider
        baseUrl={
          globalThis.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
          process.env.REDO_SHOPIFY_SERVER_URL!
        }
        storeUrl={
          globalThis.redoStorefront?.storeUrl ??
          globalThis.Shopify?.shop ??
          globalThis.Shopify?.Checkout?.apiHost
        }
        widgetId={globalThis.redoWidgetId}
      >
        <OrderTrackingLogin
          returnAppUrl={
            globalThis.redoStorefront?.env?.REDO_RETURN_APP_URL ??
            process.env.REDO_RETURN_APP_URL
          }
        />
      </RedoShopifyRpcClientContextProvider>
    </AuthenticatedShopifyExtensionClientProvider>,
  );
}
